// One endless, slowly drifting row of round cards — shared by the talent row
// ("Trusted by Industry Leaders", IndustryCarousel) and the homepage's Top
// Songs row (home/TopSongsCarousel), so both move, pause and stand still the
// same way:
//
// - Drift: 30px a second (the talent row's old 0.5px a frame at 60Hz, now
//   the same on 120Hz screens). direction 1 drifts left, -1 drifts right.
// - Endless: one set of cards is repeated as often as the screen needs, so a
//   short set never leaves a gap; the loop length is measured from the real
//   cards (the phone gap is 14px, not 20), so the jump back is invisible.
// - Pauses while a mouse is over it and while a finger is on it (plus a beat
//   after the finger lifts). Drag and fling still work; a drag never opens
//   the card it ends on.
// - Reduce Motion (or fewer than minLoop cards): no drift and one copy only,
//   a plain row the finger swipes, centred when it fits.
//
// Styles: DriftRow.css (static row) on top of Home.css's .ambassadors-* look.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./DriftRow.css";

const PX_PER_MS = 0.03; // 30px a second
const FRAME_MS = 1000 / 60; // the fling velocity is per 60Hz frame
const RESUME_AFTER_TOUCH_MS = 1200;

/** Keep the offset inside (0, 2 sets): the jump is one set, so it never shows. */
const wrap = (x: number, w: number) => (w <= 0 ? x : x >= w * 2 ? x - w : x <= 0 ? x + w : x);

const reduceMotionQuery = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)")
    : null;

/** Live prefers-reduced-motion: flips the row the moment the setting changes. */
export function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(() => !!reduceMotionQuery()?.matches);
  useEffect(() => {
    const mq = reduceMotionQuery();
    if (!mq) return;
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduce;
}

interface Props {
  /** Cards in one set (what renderSet(0) renders). */
  count: number;
  /**
   * One set of cards. `repeat` sets are for the eye only: hide them from
   * screen readers and the tab order. The readable set is the middle one,
   * which is always at least partly on screen.
   */
  renderSet: (copy: number, repeat: boolean) => React.ReactNode;
  /** 1 = drifts left (talent row), -1 = drifts right (Top Songs). */
  direction?: 1 | -1;
  /** Fewer cards than this → a still, centred row instead of a loop. */
  minLoop?: number;
  className?: string;
}

const DriftRow: React.FC<Props> = ({ count, renderSet, direction = 1, minLoop = 1, className = "" }) => {
  const reduce = useReducedMotion();
  const still = reduce || count < minLoop;
  const [copies, setCopies] = useState(3);
  const rowRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const offset = useRef(0);
  const loop = useRef(0); // width of one set, gap included
  const velocity = useRef(0); // fling, px per 60Hz frame
  const hover = useRef(false);
  const touchHoldUntil = useRef(0);
  const dragging = useRef(false);
  const lastX = useRef(0);
  const moved = useRef(0);

  const paint = useCallback(() => {
    if (trackRef.current) trackRef.current.style.transform = `translateX(${-offset.current}px)`;
  }, []);

  // Measure one set from the real cards and repeat it often enough that
  // [offset, offset + row width] is always covered while offset stays in (0, 2 sets).
  useLayoutEffect(() => {
    if (still) {
      loop.current = 0;
      if (trackRef.current) trackRef.current.style.transform = "";
      return;
    }
    const measure = () => {
      const row = rowRef.current;
      const kids = trackRef.current?.children;
      if (!row || !kids || kids.length <= count) return;
      const w = (kids[count] as HTMLElement).offsetLeft - (kids[0] as HTMLElement).offsetLeft;
      if (w <= 0) return;
      const prev = loop.current;
      loop.current = w;
      offset.current = prev > 0 ? w + ((((offset.current - prev) % w) + w) % w) : w;
      setCopies(Math.max(3, 2 + Math.ceil(row.clientWidth / w)));
      paint();
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [still, count, copies, paint]);

  // The drift itself — written straight to the track, no React render per frame.
  useEffect(() => {
    if (still) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(now - last, 64);
      last = now;
      if (!dragging.current && loop.current > 0) {
        const paused = hover.current || now < touchHoldUntil.current;
        const step = (paused ? 0 : PX_PER_MS * dt * direction) + velocity.current * (dt / FRAME_MS);
        velocity.current *= Math.pow(0.95, dt / FRAME_MS);
        if (Math.abs(velocity.current) < 0.1) velocity.current = 0;
        if (step) {
          offset.current = wrap(offset.current + step, loop.current);
          paint();
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [still, direction, paint]);

  const dragStart = (x: number) => {
    dragging.current = true;
    lastX.current = x;
    moved.current = 0;
    velocity.current = 0;
  };
  const dragMove = (x: number) => {
    if (!dragging.current) return;
    const diff = lastX.current - x;
    lastX.current = x;
    moved.current += Math.abs(diff);
    velocity.current = diff * 0.3;
    offset.current = wrap(offset.current + diff, loop.current);
    paint();
  };
  const dragEnd = () => {
    dragging.current = false;
  };

  const handlers = still
    ? {}
    : {
        onPointerEnter: (e: React.PointerEvent) => {
          if (e.pointerType === "mouse") hover.current = true;
        },
        onPointerLeave: (e: React.PointerEvent) => {
          if (e.pointerType === "mouse") hover.current = false;
          dragEnd();
        },
        onTouchStart: (e: React.TouchEvent) => {
          touchHoldUntil.current = Infinity;
          dragStart(e.touches[0].clientX);
        },
        onTouchMove: (e: React.TouchEvent) => dragMove(e.touches[0].clientX),
        onTouchEnd: () => {
          touchHoldUntil.current = performance.now() + RESUME_AFTER_TOUCH_MS;
          dragEnd();
        },
        onTouchCancel: () => {
          touchHoldUntil.current = performance.now() + RESUME_AFTER_TOUCH_MS;
          dragEnd();
        },
        onMouseDown: (e: React.MouseEvent) => dragStart(e.clientX),
        onMouseMove: (e: React.MouseEvent) => dragMove(e.clientX),
        onMouseUp: dragEnd,
        // A drag that ends on a card is a drag, not a tap.
        onClickCapture: (e: React.MouseEvent) => {
          if (moved.current > 6) {
            e.preventDefault();
            e.stopPropagation();
          }
          moved.current = 0;
        },
      };

  const sets = still ? 1 : copies;
  return (
    <div
      ref={rowRef}
      className={`ambassadors-carousel drift-row${still ? " drift-row--still" : ""}${className ? ` ${className}` : ""}`}
      data-drift={still ? "still" : direction === 1 ? "left" : "right"}
      {...handlers}
    >
      <div ref={trackRef} className="ambassadors-track-js">
        {Array.from({ length: sets }, (_, copy) => (
          <React.Fragment key={copy}>{renderSet(copy, sets > 1 && copy !== 1)}</React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default DriftRow;
